import type { AxiosError } from 'axios'
import type { ApiFailure } from '@/types/api.types'

export type AppErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'NETWORK'
  | 'TIMEOUT'
  | 'UNEXPECTED'

export class AppError extends Error {
  readonly code: AppErrorCode
  readonly status?: number
  readonly details?: Record<string, string[]>
  readonly original?: unknown

  constructor(
    message: string,
    code: AppErrorCode = 'UNEXPECTED',
    options?: {
      status?: number
      details?: Record<string, string[]>
      original?: unknown
    },
  ) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.status = options?.status
    this.details = options?.details
    this.original = options?.original
  }
}

function isAxiosLike(error: unknown): error is AxiosError<ApiFailure & { message?: string }> {
  return Boolean(
    error &&
      typeof error === 'object' &&
      ('isAxiosError' in error || 'response' in error || 'request' in error),
  )
}

/** Normalize any thrown value into a typed AppError for UI / toasts */
export function normalizeApiError(error: unknown): AppError {
  if (error instanceof AppError) return error

  if (isAxiosLike(error)) {
    if (error.code === 'ECONNABORTED') {
      return new AppError('Request timed out. Please try again.', 'TIMEOUT', {
        original: error,
      })
    }

    if (!error.response) {
      return new AppError(
        'Network error. Check your connection and try again.',
        'NETWORK',
        { original: error },
      )
    }

    const status = error.response.status
    const body = error.response.data
    const message = body?.message || error.message || 'Something went wrong'
    const details = body?.errors

    if (status === 401) {
      return new AppError(message || 'Session expired. Please sign in again.', 'UNAUTHORIZED', {
        status,
        details,
        original: error,
      })
    }
    if (status === 403) {
      return new AppError(message || 'No permission', 'FORBIDDEN', {
        status,
        details,
        original: error,
      })
    }
    if (status === 404) {
      return new AppError(message || 'Resource not found.', 'NOT_FOUND', {
        status,
        details,
        original: error,
      })
    }
    if (status === 409) {
      return new AppError(message || 'Conflict.', 'CONFLICT', {
        status,
        details,
        original: error,
      })
    }
    if (status === 422 || status === 400) {
      return new AppError(message || 'Validation failed.', 'VALIDATION', {
        status,
        details,
        original: error,
      })
    }

    return new AppError(message, 'UNEXPECTED', { status, details, original: error })
  }

  if (error instanceof Error) {
    return new AppError(error.message, 'UNEXPECTED', { original: error })
  }

  return new AppError('An unexpected error occurred.', 'UNEXPECTED', {
    original: error,
  })
}

export function getErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof AppError) return error.message
  if (error instanceof Error) return error.message
  return fallback
}

/** Machine-readable `code` from the API error body (e.g. SESSION_ENDED), if any. */
export function apiErrorCode(error: unknown): string | undefined {
  const original = error instanceof AppError ? error.original : error
  const code = (original as { response?: { data?: { code?: unknown } } } | undefined)?.response?.data?.code
  return typeof code === 'string' ? code : undefined
}
